/**
 * EN↔JA Language Pair Definition
 *
 * buildProfile() generates directional profiles:
 *   enJa = JA native → learn EN (UI in Japanese)
 *   jaEn = EN native → learn JA (UI in English)
 *
 * Phonetic: uses romaji (phonetic_us field repurposed with label "Romaji")
 * detectScript: covers kanji, hiragana, katakana Unicode ranges
 */

import type { LanguagePairDefinition, LanguageProfile } from '../types'
import { buildProfile } from '../build-profile'

const PAIR: LanguagePairDefinition = {
  id: 'en-ja',
  languages: {
    en: {
      lang: 'en',
      langName: 'English',
      shortCode: 'EN',
      scriptSample: 'Hello',
      phonetic: {
        type: 'ipa',
        fields: [
          { key: 'phonetic_us', label: 'US' },
          { key: 'phonetic_uk', label: 'UK' },
        ],
      },
      tts: {
        voices: [
          { accent: 'us', lang: 'en-US' },
          { accent: 'uk', lang: 'en-GB' },
        ],
        detectScript: /^$/,
      },
      ui: {
        inputPlaceholder: 'Enter words, phrases, or questions...',
        headingText: 'What do you want to learn today?',
        intentChips: [
          { label: 'List', intent: 'BATCH_LIST', suffix: 'how to say in Japanese' },
          { label: 'Compare', intent: 'CONFUSABLE_PAIRS', type: 'compare', separator: 'vs' },
        ],
      },
      promptConfig: {
        classifierHint: 'User may input in English.',
      },
    },
    ja: {
      lang: 'ja',
      langName: 'Japanese',
      shortCode: 'JA',
      scriptSample: 'こんにちは',
      phonetic: {
        type: 'romaji',
        fields: [
          { key: 'phonetic_us', label: 'Romaji' },
        ],
      },
      tts: {
        voices: [
          { accent: 'ja', lang: 'ja-JP' },
        ],
        detectScript: /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\u3400-\u4DBF]/,
      },
      ui: {
        inputPlaceholder: '単語、フレーズ、質問を入力...',
        headingText: '今日は何を学びますか？',
        intentChips: [
          { label: 'リスト', intent: 'BATCH_LIST', suffix: '英語でなんと言う' },
          { label: '比較', intent: 'CONFUSABLE_PAIRS', type: 'compare', separator: 'vs' },
        ],
      },
      promptConfig: {
        classifierHint: 'User may input in Japanese (hiragana, katakana, kanji, or romaji).',
        languageHints: [
          'For words with kanji, include the reading in romaji in phonetic_us field.',
          'For verbs, show the dictionary form (辞書形) and te-form (て形) in collocations.',
          'Tag politeness level: register:casual (plain form), register:polite (teineigo), register:humble (kenjougo).',
          'For counters, include the counter word in example sentences (e.g. 一匹、二匹).',
          'When phonetic type is romaji, write romaji in phonetic_us. Leave phonetic_uk empty.',
        ].join('\n'),
      },
      cardMeta: {
        keys: ['kanji', 'hiragana', 'verbForm'],
        promptDescription: `"card_meta" (JSON object, required): Japanese-specific metadata:
- "kanji": kanji writing of the word.
- "hiragana": hiragana reading.
- "verbForm": object with "teForm" and optional "dictionaryForm". Only for verbs.`,
      },
    },
  },
}

export const enJaPair = PAIR
export const enJa: LanguageProfile = buildProfile(PAIR, 'en')
export const jaEn: LanguageProfile = buildProfile(PAIR, 'ja')
